import { getDocument, OPS } from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { withTimeout } from './worker';

import { collapseWarnings, type ConversionWarning } from '../adapter';
import { emptyDocument, newEntityId, type CadDocument } from '../../model/document';
import { flattenCubicBezier, type Pt } from '../../model/geom';
import { UM_PER_MM } from '../../model/units';
import type { CadEntity, LineEntity, PolylineEntity, TextEntity } from '../../model/entity';

/**
 * PDF → 편집 가능한 CAD 엔티티 (PDFIMPORT, 작업지시서 외 신규 요청)
 *
 * ★ 축척 문제 — PDF 좌표(pt, 1/72인치)는 종이 크기이지 실제 매장 치수가 아니다.
 *   AutoCAD "1:100 로 A1 용지에 인쇄"한 PDF 를 그대로 pt→mm 변환하면 도면이
 *   종이 한 장 크기로 들어온다. 실제 AutoCAD PDFIMPORT 도 자동 보정을 하지
 *   않고 사용자가 배율을 알려주게 되어 있다 — 여기서도 scaleDenom(1:N) 을
 *   호출자(가져오기 UI)에게서 받는다.
 *
 * ★ 경로 읽기 — pdfjs 의 getOperatorList() 는 PDF 컨텐츠 스트림의 m/l/c/v/y/re/h
 *   연산자를 전부 OPS.constructPath 하나로 병합해 넘긴다. 그 인자는
 *   `[paintFn, [Float32Array | null], minMax]` 이고, Float32Array 안에는
 *   {0:moveTo, 1:lineTo, 2:curveTo(6개), 3:quadraticCurveTo(4개), 4:closePath}
 *   코드로 인코딩된 좌표가 들어 있다(이 값은 pdfjs 소스의 buildPath()/
 *   makePathFromDrawOPS() 로 직접 확인했다 — 라이브러리 버전에 따라 바뀔 수
 *   있는 내부 구조이니, pdfjs-dist 를 올릴 때는 이 부분을 다시 확인할 것).
 *   이 좌표들은 CTM 적용 전(로컬) 좌표라서, save/restore/transform 오퍼레이터를
 *   직접 스택으로 추적해 페이지 좌표로 바꿔야 한다.
 */

export interface PdfImportOptions {
  /** 1:scaleDenom 축척. 예: 100 → 이 PDF 는 1:100 로 인쇄된 것. */
  scaleDenom: number;
  /** 0-based 페이지 인덱스. 기본 0(첫 페이지). */
  pageIndex?: number;
}

const PDF_LAYER = 'A-REF';

interface Matrix {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}
const IDENTITY: Matrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

function applyMatrix(m: Matrix, x: number, y: number): { x: number; y: number } {
  return { x: m.a * x + m.c * y + m.e, y: m.b * x + m.d * y + m.f };
}

/** base 뒤에 m 을 이어붙인다 — 점은 먼저 m, 그 다음 base 순으로 변환된다. */
function composeMatrix(base: Matrix, m: Matrix): Matrix {
  return {
    a: base.a * m.a + base.c * m.b,
    b: base.b * m.a + base.d * m.b,
    c: base.a * m.c + base.c * m.d,
    d: base.b * m.c + base.d * m.d,
    e: base.a * m.e + base.c * m.f + base.e,
    f: base.b * m.e + base.d * m.f + base.f,
  };
}

const DRAW_MOVE = 0;
const DRAW_LINE = 1;
const DRAW_CURVE = 2;
const DRAW_QUAD = 3;
const DRAW_CLOSE = 4;

/** 경로 데이터(Float32Array) 를 페이지 좌표(µm) 서브패스 목록으로 푼다. */
function decodeSubpaths(
  data: Float32Array,
  ctm: Matrix,
  toUm: (ptVal: number) => number
): { subpaths: Pt[][]; hadCurve: boolean } {
  const toWorld = (x: number, y: number): Pt => {
    const p = applyMatrix(ctm, x, y);
    return { x: toUm(p.x), y: toUm(p.y) };
  };

  const subpaths: Pt[][] = [];
  let current: Pt[] = [];
  let curX = 0;
  let curY = 0;
  let hadCurve = false;
  let i = 0;
  const next = () => data[i++] ?? 0;

  while (i < data.length) {
    const op = next();
    switch (op) {
      case DRAW_MOVE: {
        if (current.length > 1) subpaths.push(current);
        curX = next();
        curY = next();
        current = [toWorld(curX, curY)];
        break;
      }
      case DRAW_LINE: {
        curX = next();
        curY = next();
        current.push(toWorld(curX, curY));
        break;
      }
      case DRAW_CURVE: {
        const x1 = next();
        const y1 = next();
        const x2 = next();
        const y2 = next();
        const x = next();
        const y = next();
        const p0 = toWorld(curX, curY);
        const p1 = toWorld(x1, y1);
        const p2 = toWorld(x2, y2);
        const p3 = toWorld(x, y);
        for (const p of flattenCubicBezier(p0, p1, p2, p3).slice(1)) current.push(p);
        hadCurve = true;
        curX = x;
        curY = y;
        break;
      }
      case DRAW_QUAD: {
        // 2차 베지어 → 3차로 승격 (De Casteljau 재사용을 위해)
        const x1 = next();
        const y1 = next();
        const x = next();
        const y = next();
        const c1x = curX + (2 / 3) * (x1 - curX);
        const c1y = curY + (2 / 3) * (y1 - curY);
        const c2x = x + (2 / 3) * (x1 - x);
        const c2y = y + (2 / 3) * (y1 - y);
        const p0 = toWorld(curX, curY);
        const p1 = toWorld(c1x, c1y);
        const p2 = toWorld(c2x, c2y);
        const p3 = toWorld(x, y);
        for (const p of flattenCubicBezier(p0, p1, p2, p3).slice(1)) current.push(p);
        hadCurve = true;
        curX = x;
        curY = y;
        break;
      }
      case DRAW_CLOSE: {
        if (current.length > 0) current.push({ ...current[0]! });
        break;
      }
      default:
        break;
    }
  }
  if (current.length > 1) subpaths.push(current);
  return { subpaths, hadCurve };
}

function subpathToEntity(points: Pt[]): CadEntity | null {
  const rounded = points.map((p) => ({ x: Math.round(p.x), y: Math.round(p.y) }));
  const dedup: Pt[] = [];
  for (const p of rounded) {
    const last = dedup[dedup.length - 1];
    if (!last || last.x !== p.x || last.y !== p.y) dedup.push(p);
  }
  if (dedup.length < 2) return null;

  const first = dedup[0]!;
  const last = dedup[dedup.length - 1]!;
  const closed = dedup.length > 2 && first.x === last.x && first.y === last.y;
  const pts = closed ? dedup.slice(0, -1) : dedup;

  if (pts.length === 2 && !closed) {
    const line: LineEntity = { id: newEntityId(), type: 'line', layer: PDF_LAYER, a: pts[0]!, b: pts[1]! };
    return line;
  }
  const poly: PolylineEntity = {
    id: newEntityId(),
    type: 'polyline',
    layer: PDF_LAYER,
    points: pts,
    closed,
  };
  return poly;
}

const STROKE_FNS = new Set<number>([
  OPS.stroke,
  OPS.closeStroke,
  OPS.fillStroke,
  OPS.eoFillStroke,
  OPS.closeFillStroke,
  OPS.closeEOFillStroke,
]);
const FILL_FNS = new Set<number>([
  OPS.fill,
  OPS.eoFill,
  OPS.fillStroke,
  OPS.eoFillStroke,
  OPS.closeFillStroke,
  OPS.closeEOFillStroke,
]);
const RASTER_FNS = new Set<number>([
  OPS.paintImageXObject,
  OPS.paintInlineImageXObject,
  OPS.paintImageXObjectRepeat,
  OPS.paintImageMaskXObject,
  OPS.paintImageMaskXObjectGroup,
  OPS.paintInlineImageXObjectGroup,
  OPS.paintImageMaskXObjectRepeat,
  OPS.paintSolidColorImageMask,
]);

async function collectVectorEntities(
  page: PDFPageProxy,
  toUm: (ptVal: number) => number
): Promise<{ entities: CadEntity[]; hadCurve: boolean; hadRaster: boolean }> {
  const { fnArray, argsArray } = await page.getOperatorList();
  const entities: CadEntity[] = [];
  let hadCurve = false;
  let hadRaster = false;
  const ctmStack: Matrix[] = [];
  let ctm: Matrix = IDENTITY;

  for (let i = 0; i < fnArray.length; i++) {
    const fn = fnArray[i];
    const args = argsArray[i] as unknown[] | undefined;

    if (fn === OPS.save) {
      ctmStack.push(ctm);
    } else if (fn === OPS.restore) {
      ctm = ctmStack.pop() ?? IDENTITY;
    } else if (fn === OPS.transform) {
      const [a, b, c, d, e, f] = (args ?? []) as number[];
      ctm = composeMatrix(ctm, {
        a: a ?? 1,
        b: b ?? 0,
        c: c ?? 0,
        d: d ?? 1,
        e: e ?? 0,
        f: f ?? 0,
      });
    } else if (fn === OPS.constructPath) {
      const pathFn = (args?.[0] as number | undefined) ?? -1;
      const dataList = args?.[1] as (Float32Array | null)[] | undefined;
      const data = dataList?.[0];
      if (!data) continue;
      const stroke = STROKE_FNS.has(pathFn);
      const fill = FILL_FNS.has(pathFn);
      if (!stroke && !fill) continue; // 클리핑 전용 등 실제로 칠하지 않는 경로는 건너뛴다

      const { subpaths, hadCurve: curved } = decodeSubpaths(data, ctm, toUm);
      if (curved) hadCurve = true;
      for (const sp of subpaths) {
        const ent = subpathToEntity(sp);
        if (ent) entities.push(ent);
      }
    } else if (fn === OPS.paintFormXObjectBegin) {
      // Form XObject(블록·심볼 인스턴스 등)는 자기만의 /Matrix 를 가질 수 있고,
      // PDF 스펙상 그 시작 시점에 암묵적으로 save, 끝나면 restore 된다.
      // 이걸 안 하면(예전 버그) 블록으로 들어간 도형만 축척이 틀어진다 —
      // 오토캐드 PDF 는 거의 모든 도형을 Form XObject 로 내보내므로 영향이 크다.
      ctmStack.push(ctm);
      const matrix = args?.[0] as Float32Array | number[] | null | undefined;
      if (matrix && matrix.length === 6) {
        const [a, b, c, d, e, f] = matrix;
        ctm = composeMatrix(ctm, {
          a: a ?? 1,
          b: b ?? 0,
          c: c ?? 0,
          d: d ?? 1,
          e: e ?? 0,
          f: f ?? 0,
        });
      }
    } else if (fn === OPS.paintFormXObjectEnd) {
      ctm = ctmStack.pop() ?? IDENTITY;
    } else if (fn !== undefined && RASTER_FNS.has(fn)) {
      hadRaster = true;
    }
  }

  return { entities, hadCurve, hadRaster };
}

async function collectTextEntities(
  page: PDFPageProxy,
  toUm: (ptVal: number) => number
): Promise<TextEntity[]> {
  const content = await page.getTextContent();
  const out: TextEntity[] = [];
  for (const item of content.items) {
    if (!('str' in item) || !item.str.trim()) continue;
    const [a, b, c, d, e, f] = item.transform as number[];
    if (a === undefined || b === undefined || c === undefined || d === undefined) continue;
    if (e === undefined || f === undefined) continue;

    const heightPt = Math.hypot(c, d) || Math.hypot(a, b) || 10;
    const text: TextEntity = {
      id: newEntityId(),
      type: 'text',
      layer: PDF_LAYER,
      at: { x: Math.round(toUm(e)), y: Math.round(toUm(f)) },
      text: item.str,
      height: Math.max(1, Math.round(toUm(heightPt))),
      rotation: Math.atan2(b, a),
      align: 'left',
    };
    out.push(text);
  }
  return out;
}

/** 편의 함수 — dxfAdapter.parseDxfFile 과 같은 모양 */
export async function parsePdfFile(
  file: File | Blob,
  opts: PdfImportOptions
): Promise<{ doc: CadDocument; warnings: ConversionWarning[] }> {
  const buf = await file.arrayBuffer();
  const loadingTask = getDocument({ data: buf });
  const pdf: PDFDocumentProxy = await withTimeout(loadingTask.promise, 20000, 'PDF 열기');
  const warnings: ConversionWarning[] = [];

  try {
    const pageIndex = opts.pageIndex ?? 0;
    if (pdf.numPages > 1) {
      warnings.push({
        code: 'unsupported_entity',
        subject: 'PDF 페이지',
        message: `이 PDF 는 총 ${pdf.numPages}페이지입니다. ${pageIndex + 1}페이지만 가져왔습니다.`,
      });
    }

    const page = await pdf.getPage(pageIndex + 1); // pdfjs 는 1-based

    // PDF 의 /UserUnit — 기본 사용자 공간 1단위가 실제로 몇 인치인지 정하는
    // 페이지 속성이다(기본 1 = 1/72인치). AutoCAD 는 대형 도면을 내보낼 때
    // 이 값을 1이 아닌 값으로 쓰기도 한다. getOperatorList()/getTextContent()
    // 가 주는 좌표는 이 값과 무관한 "기본 사용자 공간" 좌표이므로, 실세계
    // mm 로 바꾸는 단계에서 반드시 곱해 줘야 한다 — 이걸 빠뜨리면 도면 전체가
    // 일정한 비율로 어긋난다(치수가 실제와 다르게 나오는 원인).
    const mmPerPt = (25.4 / 72) * opts.scaleDenom * page.userUnit;
    const toUm = (ptVal: number) => ptVal * mmPerPt * UM_PER_MM;

    const [{ entities: vectorEntities, hadCurve, hadRaster }, textEntities] = await Promise.all([
      collectVectorEntities(page, toUm),
      collectTextEntities(page, toUm),
    ]);

    if (hadCurve) {
      warnings.push({
        code: 'curve_approximated',
        subject: '곡선 경로',
        message: '곡선 경로를 다각형으로 근사했습니다.',
      });
    }
    if (hadRaster) {
      warnings.push({
        code: 'raster_skipped',
        subject: 'PDF 이미지',
        message:
          'PDF 안 래스터 이미지는 벡터로 변환하지 못했습니다. 배경으로 쓰려면 부착(Attach) 기능을 쓰세요.',
      });
    }

    const doc: CadDocument = {
      ...emptyDocument('mm'),
      entities: [...vectorEntities, ...textEntities],
    };
    return { doc, warnings: collapseWarnings(warnings) };
  } finally {
    await loadingTask.destroy();
  }
}
